import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MdReceipt, MdLocalPharmacy, MdEvent } from "react-icons/md";
import SearchInput from "../../clinic/components/SearchInput";

function Appointments() {
    const location = useLocation();
    const preViousState = location?.state ?? {};

    const [activeTab, setActiveTab] = useState(preViousState.activeTab || "appointments");
    const [filterInput, setFilterInput] = useState("");

    const appointments = [
        {
            id: 1,
            date: "2021-09-01",
            time: "10:00 AM",
            doctor: "Dr. John Doe",
            status: "completed",
            patient: "John Doe",
        },
        {
            id: 2,
            date: "2021-09-15",
            time: "11:00 AM",
            doctor: "Dr. Jane Doe",
            status: "pending",
            patient: "Jane Doe",
        },
    ];

    const prescriptions = [
        {
            id: 1,
            date: "2021-09-01",
            doctor: "Dr. John Doe",
            medicine: "Paracetamol",
            dosage: "500mg",
            frequency: "1-1-1",
            duration: "5 days",
            url: "https://example.com/prescription-1",
        },
        {
            id: 2,
            date: "2021-09-15",
            doctor: "Dr. Jane Doe",
            medicine: "Ibuprofen",
            dosage: "400mg",
            frequency: "1-0-1",
            duration: "3 days",
            url: "https://example.com/prescription-2",
        },
    ];

    const files = [
        {
            id: 1,
            date: "2021-09-01",
            doctor: "Dr. John Doe",
            type: "X-Ray",
            url: "https://example.com/x-ray-1",
        },
        {
            id: 2,
            date: "2021-09-15",
            doctor: "Dr. Jane Doe",
            type: "MRI",
            url: "https://example.com/mri-1",
        },
    ];

    const handleFilterChange = (event) => {
        const value = event.target.value || "";
        setFilterInput(value);
    };

    const filterData = (data, fields) => {
        const lowerCaseFilterInput = filterInput.toLowerCase();
        return data.filter(item =>
            fields.some(field =>
                item[field].toLowerCase().includes(lowerCaseFilterInput)
            )
        );
    };    

    const renderTabContent = () => {
        const filteredAppointments = filterData(appointments, ['doctor', 'patient']);
        const filteredPrescriptions = filterData(prescriptions, ['doctor', 'medicine']);
        const filteredFiles = filterData(files, ['doctor', 'type']);
        
        switch (activeTab) {
            case "appointments":
                return renderAppointments(filteredAppointments);
            case "prescriptions":
                return renderPrescriptions(filteredPrescriptions);
            case "files":
                return renderFiles(filteredFiles);
          default:
            return null;
        }
    };

    const renderAppointments = (appointments) => (
        <div className="space-y-4">
            {appointments.map((appointment) => (
                <div
                    key={appointment.id}
                    className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm"
                >
                    <div>
                        <h3 className="text-lg font-medium">{appointment.doctor}</h3>
                        <p className="text-sm text-gray-500">{appointment.date}</p>
                        <p className="text-sm text-gray-500">{appointment.time}</p>
                        <p className="text-sm text-gray-500">{appointment.patient}</p>
                    </div>
                    <a
                        href="#"
                        className={`px-3 py-1 text-xs font-medium rounded-full ${
                            appointment.status === "completed"
                            ? "bg-green-500 text-white"
                            : "bg-blue-500 text-white"
                        }`}
                    >
                        {appointment.status === "completed" ? "Completed" : "Pending"}
                    </a>
                </div>
            ))}
        </div>
    );
    
    const renderPrescriptions = (prescriptions) => (
        <div className="space-y-4">
            {prescriptions.map((prescription) => (
                <div
                    key={prescription.id}
                    className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm"
                >
                    <div>
                        <h3 className="text-lg font-medium">{prescription.doctor}</h3>
                        <p className="text-sm text-gray-500">{prescription.date}</p>
                        <p className="text-sm text-gray-500">
                            {prescription.medicine} - {prescription.dosage}
                        </p>
                        <p className="text-sm text-gray-500">
                            {prescription.frequency} - {prescription.duration}
                        </p>
                    </div>
                    <a
                        href={prescription.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 text-xs font-medium bg-blue-500 text-white rounded-full"
                        >
                        <MdReceipt className="inline mr-2" />
                        View
                    </a>
                </div>
            ))}
        </div>
    );
    
    const renderFiles = (files) => (
        <div className="space-y-4">
            {files.map((file) => (
            <div
                key={file.id}
                className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm"
            >
                <div>
                    <h3 className="text-lg font-medium">{file.doctor}</h3>
                    <p className="text-sm text-gray-500">{file.date}</p>
                    <p className="text-sm text-gray-500">{file.type}</p>
                </div>
                <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 text-xs font-medium bg-blue-500 text-white rounded-full"
                >
                    <MdReceipt className="inline mr-2" />
                    View
                </a>
            </div>
            ))}
        </div>
    );

    return (
        <div className="flex flex-col h-[calc(100vh-72px)] p-5 bg-[#ECF1F4]">
            <motion.div
                initial={{ y: 50 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                exit={{ opacity: 0, y: 50 }}
                className="my-6 flex flex-col gap-4"
            >
                <nav className="flex flex-wrap gap-x-3 mb-0 border-b border-gray-200 px-2 py-2 bg-gray-50">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`flex-1 py-2 px-4 text-sm font-medium text-center rounded-t-lg border-b-2 ${
                        activeTab === "appointments"
                            ? "bg-green-500 text-white border-green-500 shadow-xl"
                            : "text-gray-500 bg-white border-transparent shadow-sm"
                        }`}
                        onClick={() => setActiveTab("appointments")}
                    >
                        <MdEvent className="inline mr-2 font" size={20} />
                        <span className="hidden sm:inline">Appointments</span>
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`flex-1 py-2 px-4 text-sm font-medium text-center rounded-t-lg border-b-2 ${
                        activeTab === "prescriptions"
                            ? "bg-red-500 text-white border-red-500 shadow-xl"
                            : "text-gray-500 bg-white border-transparent shadow-sm"
                        }`}
                        onClick={() => setActiveTab("prescriptions")}
                    >
                        <MdLocalPharmacy className="inline mr-2 font" size={20} />
                        <span className="hidden sm:inline">Prescriptions</span>
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`flex-1 py-2 px-4 text-sm font-medium text-center rounded-t-lg border-b-2 ${
                        activeTab === "files"
                            ? "bg-blue-500 text-white border-blue-500 shadow-xl"
                            : "text-gray-500 bg-white border-transparent shadow-sm"
                        }`}
                        onClick={() => setActiveTab("files")}
                    >
                        <MdReceipt className="inline mr-2 font" size={20} />
                        <span className="hidden sm:inline">Files</span>
                    </motion.button>
                </nav>
                
                <SearchInput
                    filterInput={filterInput}
                    handleFilterChange={handleFilterChange}
                    className="w-full xl:w-full"
                />

                <AnimatePresence>
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                        className="tab-content"
                    >
                        {renderTabContent()}
                    </motion.div>
                </AnimatePresence>
            </motion.div>
        </div>
    );
}

export default Appointments;
